import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Activity, ChevronRight, Database, HardDriveDownload, HardDriveUpload, Loader2, Network, Plus, Signal, Trash2 } from "lucide-react";
import { api, dt, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { InfraMap } from "@/components/InfraMap";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TYPES = [
  ["server", "Server / POP"],
  ["backbone", "Jalur Backbone"],
  ["odc", "ODC"],
  ["odp", "ODP"],
  ["onu", "ONU / Client"],
];
const TYPE_CLS = {
  server: "border-violet-500/40 bg-violet-500/10 text-violet-300",
  backbone: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  odc: "border-cyan-500/40 bg-cyan-500/10 text-cyan-300",
  odp: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  onu: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
};
const SIGNAL_CLS = { baik: "text-emerald-400", waspada: "text-amber-400", buruk: "text-red-400", unknown: "text-muted-foreground" };

const Field = ({ label, ...props }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>
    <Input className="h-11 rounded-xl bg-secondary/50" {...props} />
  </div>
);

const EMPTY = { name: "", type: "odp", parent_id: "", cores: 0, ports: 0, secret_name: "", address: "", note: "", lat: "", lng: "" };

const Stat = ({ label, value, cls }) => (
  <div className={`rounded-2xl border bg-gradient-to-br p-4 ${cls}`}>
    <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
    <p className="mt-1 font-display text-2xl font-semibold">{value}</p>
  </div>
);

const flatten = (kids, parent, depth, out) => {
  (kids[parent] || []).forEach((n) => {
    out.push({ ...n, depth });
    flatten(kids, n.id, depth + 1, out);
  });
  return out;
};

const NodeRow = ({ n, depth, onEdit, onSignal, onRemove }) => (
  <div style={{ marginLeft: depth * 18 }} data-testid={`infra-node-${n.name}`}>
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card/70 px-3 py-2">
      {depth > 0 && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
      <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase ${TYPE_CLS[n.type]}`}>{n.type}</span>
      <span className="text-sm font-medium">{n.name}</span>
      {["backbone", "odc"].includes(n.type) && <span className="text-xs text-muted-foreground">{n.cores} core</span>}
      {["odc", "odp"].includes(n.type) && (
        <span className="text-xs text-muted-foreground" data-testid={`ports-${n.name}`}>port {n.used_ports}/{n.ports}</span>
      )}
      {n.type === "onu" && (
        <>
          <span className={`rounded-full border px-2 py-0.5 text-[10px] ${n.online ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"}`} data-testid={`onu-state-${n.name}`}>
            {n.online ? "AKTIF" : "TIDAK AKTIF"}
          </span>
          <span className={`text-xs ${SIGNAL_CLS[n.signal_state]}`} data-testid={`onu-signal-${n.name}`}>
            <Signal className="mr-1 inline h-3 w-3" />{n.rx_power != null ? `${n.rx_power} dBm` : "belum diukur"}
          </span>
          {n.secret_name && <span className="font-mono text-[11px] text-muted-foreground">{n.secret_name}</span>}
        </>
      )}
      <div className="ml-auto flex gap-1">
        {n.type === "onu" && (
          <Button size="sm" variant="outline" className="h-7 rounded-full px-2 text-[11px]" data-testid={`signal-btn-${n.name}`} onClick={() => onSignal(n)}>
            Redaman
          </Button>
        )}
        <Button size="sm" variant="outline" className="h-7 rounded-full px-2 text-[11px]" onClick={() => onEdit(n)} data-testid={`edit-node-${n.name}`}>Ubah</Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => onRemove(n)} data-testid={`delete-node-${n.name}`}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  </div>
);

export default function Infra() {
  const [tab, setTab] = useState("tree");
  const [nodes, setNodes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [secrets, setSecrets] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [edit, setEdit] = useState(null);
  const [signal, setSignal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bk, setBk] = useState({ open: false, file: null, mode: "merge", result: null });
  const [snaps, setSnaps] = useState([]);
  const [snapNote, setSnapNote] = useState("");
  const [imp, setImp] = useState({ file: null, update: false, preview: null, previewing: false, result: null });

  const load = async () => {
    try {
      const [n, s, sec, sn] = await Promise.all([
        api.get("/infra/nodes"), api.get("/infra/summary"), api.get("/ppp/secrets"),
        api.get("/infra/snapshots").catch(() => ({ data: [] })),
      ]);
      setNodes(n.data); setSummary(s.data); setSecrets(sec.data); setSnaps(sn.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, []);

  const children = useMemo(() => {
    const m = {};
    nodes.forEach((n) => { const p = n.parent_id || "root"; (m[p] = m[p] || []).push(n); });
    return m;
  }, [nodes]);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/infra/nodes", {
        ...form, cores: Number(form.cores), ports: Number(form.ports),
        lat: form.lat === "" ? null : Number(form.lat), lng: form.lng === "" ? null : Number(form.lng),
      });
      toast.success(`${form.type.toUpperCase()} ${form.name} ditambahkan`); setForm({ ...EMPTY, type: form.type, parent_id: form.parent_id }); setTab("tree"); load();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/infra/nodes/${edit.id}`, {
        name: edit.name, type: edit.type, parent_id: edit.parent_id || "", cores: Number(edit.cores || 0),
        ports: Number(edit.ports || 0), secret_name: edit.secret_name || "", address: edit.address || "", note: edit.note || "",
        lat: edit.lat === "" || edit.lat == null ? null : Number(edit.lat),
        lng: edit.lng === "" || edit.lng == null ? null : Number(edit.lng),
      });
      toast.success("Perubahan disimpan"); setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveSignal = async () => {
    setBusy(true);
    try {
      await api.put(`/infra/nodes/${signal.id}/signal`, { rx_power: Number(signal.rx_power), note: signal.note || "" });
      toast.success("Redaman disimpan"); setSignal(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const downloadBackup = async () => {
    setBusy(true);
    try {
      const res = await api.get("/infra/backup", { responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1]
        || `alphabill-infrastruktur-${new Date().toISOString().slice(0, 10)}.json`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup infrastruktur diunduh");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const restoreBackup = async () => {
    if (!bk.file) return toast.error("Pilih file backup dulu");
    if (bk.mode === "replace" && !window.confirm("Mode Ganti Total akan MENGHAPUS seluruh data infrastruktur saat ini lalu menggantinya dengan isi file. Lanjutkan?")) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", bk.file);
      fd.append("mode", bk.mode);
      const { data } = await api.post("/infra/restore", fd, { timeout: 300000 });
      setBk((b) => ({ ...b, result: data }));
      toast.success(`Restore selesai: ${data.restored} node dipulihkan`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const makeSnapshot = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("note", snapNote);
      const { data } = await api.post("/infra/snapshots", fd);
      toast.success(`Snapshot tersimpan (${data.total} node)`);
      setSnapNote(""); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const snapDownload = async (sn) => {
    try {
      const res = await api.get(`/infra/snapshots/${sn._id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = sn.filename || `${sn._id}.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const snapRestore = async (sn) => {
    if (!window.confirm(`Pulihkan infrastruktur dari snapshot ${dt(sn.created_at)} dengan mode ${bk.mode === "replace" ? "Ganti Total" : "Gabung"}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/infra/snapshots/${sn._id}/restore`, null, { params: { mode: bk.mode }, timeout: 300000 });
      toast.success(`Restore selesai: ${data.restored} node dipulihkan`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const snapDelete = async (sn) => {
    try { await api.delete(`/infra/snapshots/${sn._id}`); toast.success("Snapshot dihapus"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const previewCsv = async (file, update) => {
    if (!file) return;
    setImp((c) => ({ ...c, previewing: true, preview: null, result: null }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("update_existing", (update ?? imp.update) ? "true" : "false");
      fd.append("dry_run", "true");
      const { data } = await api.post("/infra/import-csv", fd, { timeout: 300000 });
      setImp((c) => ({ ...c, preview: data }));
    } catch (e) { toast.error(errMsg(e)); } finally { setImp((c) => ({ ...c, previewing: false })); }
  };

  const importCsv = async () => {
    if (!imp.file) return toast.error("Pilih file CSV dulu");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", imp.file);
      fd.append("update_existing", imp.update ? "true" : "false");
      const { data } = await api.post("/infra/import-csv", fd, { timeout: 300000 });
      setImp((c) => ({ ...c, result: data, preview: null }));
      toast.success(`Impor selesai: ${data.created} node baru, ${data.updated} diperbarui`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/infra/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "template-infrastruktur.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (n) => {
    if (!window.confirm(`Hapus ${n.type.toUpperCase()} ${n.name}?`)) return;
    try { await api.delete(`/infra/nodes/${n.id}`); toast.success("Node dihapus"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Tabs value={tab} onValueChange={setTab} className="fade-up space-y-6">
      <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-xl bg-secondary/60 p-1">
        {[["tree", "Skema Jaringan"], ["map", "Peta"], ["list", "Daftar Node"], ["new", "Tambah Node"], ["import", "Impor CSV"], ["backup", "Backup & Restore"]].map(([v, l]) => (
          <TabsTrigger key={v} value={v} data-testid={`tab-infra-${v}`} className="rounded-lg px-4 py-2 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500/20 data-[state=active]:to-violet-600/20">{l}</TabsTrigger>
        ))}
      </TabsList>

      {summary && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="infra-summary">
          <Stat label="Server / POP" value={summary.counts.server} cls="border-violet-500/30 from-violet-500/15 to-transparent" />
          <Stat label="ODC / ODP" value={`${summary.counts.odc} / ${summary.counts.odp}`} cls="border-cyan-500/30 from-cyan-500/15 to-transparent" />
          <Stat label="Port terpakai" value={`${summary.used_ports}/${summary.total_ports}`} cls="border-amber-500/30 from-amber-500/15 to-transparent" />
          <Stat label="ONU aktif" value={`${summary.onu_online}/${summary.counts.onu}`} cls="border-emerald-500/30 from-emerald-500/15 to-transparent" />
          <Stat label="Sinyal buruk" value={summary.signal_bad} cls="border-red-500/30 from-red-500/15 to-transparent" />
        </div>
      )}

      <TabsContent value="tree">
        <div className="space-y-2 rounded-2xl border border-border bg-card p-4" data-testid="infra-tree">
          {flatten(children, "root", 0, []).map((n) => (
            <NodeRow
              key={n.id} n={n} depth={n.depth} onEdit={setEdit} onRemove={remove}
              onSignal={(x) => setSignal({ id: x.id, name: x.name, rx_power: x.rx_power ?? -22, note: x.rx_note || "" })}
            />
          ))}
          {!nodes.length && (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {loading ? "Memuat…" : "Belum ada data infrastruktur. Mulai dari Server/POP, lalu Backbone → ODC → ODP → ONU."}
            </p>
          )}
        </div>
      </TabsContent>

      <TabsContent value="map">
        <InfraMap nodes={nodes} />
      </TabsContent>

      <TabsContent value="list">
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
          <Table data-testid="infra-table">
            <TableHeader><TableRow>
              <TableHead>Nama</TableHead><TableHead>Jenis</TableHead><TableHead>Induk</TableHead><TableHead>Core</TableHead>
              <TableHead>Port</TableHead><TableHead>Secret PPPoE</TableHead><TableHead>Status</TableHead><TableHead>Redaman</TableHead><TableHead>Diukur</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {nodes.map((n) => (
                <TableRow key={n.id} className="text-xs">
                  <TableCell data-label="Nama" className="font-medium">{n.name}</TableCell>
                  <TableCell data-label="Jenis"><span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase ${TYPE_CLS[n.type]}`}>{n.type}</span></TableCell>
                  <TableCell data-label="Induk">{nodes.find((x) => x.id === n.parent_id)?.name || "-"}</TableCell>
                  <TableCell data-label="Core">{n.cores || "-"}</TableCell>
                  <TableCell data-label="Port">{["odc", "odp"].includes(n.type) ? `${n.used_ports}/${n.ports}` : "-"}</TableCell>
                  <TableCell data-label="Secret PPPoE" className="font-mono">{n.secret_name || "-"}</TableCell>
                  <TableCell data-label="Status">{n.type === "onu" ? (n.online ? "AKTIF" : "TIDAK AKTIF") : "-"}</TableCell>
                  <TableCell data-label="Redaman" className={SIGNAL_CLS[n.signal_state]}>{n.rx_power != null ? `${n.rx_power} dBm` : "-"}</TableCell>
                  <TableCell data-label="Diukur">{n.rx_updated_at ? dt(n.rx_updated_at) : "-"}</TableCell>
                </TableRow>
              ))}
              {!nodes.length && <TableRow><TableCell colSpan={9} className="py-10 text-center text-sm text-muted-foreground cell-empty">{loading ? "Memuat…" : "Belum ada node"}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="new">
        <form onSubmit={create} className="max-w-2xl space-y-5 rounded-2xl border border-border bg-card p-6" data-testid="infra-form">
          <h3 className="flex items-center gap-2 font-display text-lg font-semibold"><Network className="h-4 w-4" /> Tambah Node Infrastruktur</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Jenis</Label>
              <select className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="node-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <Field label="Nama / Label" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="node-name" />
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Induk (parent)</Label>
              <select className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="node-parent" value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })}>
                <option value="">— tanpa induk (root) —</option>
                {nodes.map((n) => <option key={n.id} value={n.id}>{n.type.toUpperCase()} · {n.name}</option>)}
              </select>
            </div>
            {["backbone", "odc"].includes(form.type) && (
              <Field label="Jumlah Core" type="number" min="0" value={form.cores} onChange={(e) => setForm({ ...form, cores: e.target.value })} data-testid="node-cores" />
            )}
            {["odc", "odp"].includes(form.type) && (
              <Field label="Jumlah Port" type="number" min="0" value={form.ports} onChange={(e) => setForm({ ...form, ports: e.target.value })} data-testid="node-ports" />
            )}
            {form.type === "onu" && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Secret PPPoE</Label>
                <select className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="node-secret" value={form.secret_name} onChange={(e) => setForm({ ...form, secret_name: e.target.value })}>
                  <option value="">— belum dikaitkan —</option>
                  {secrets.map((s) => <option key={s.id || s.username} value={s.username || s.name}>{s.username || s.name}</option>)}
                </select>
              </div>
            )}
            <Field label="Alamat / Lokasi" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="node-address" />
            <Field label="Latitude" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} data-testid="node-lat" />
            <Field label="Longitude" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} data-testid="node-lng" />
            <div className="flex items-end">
              <Button
                type="button" variant="outline" className="h-11 w-full rounded-xl" data-testid="node-geo"
                onClick={() => navigator.geolocation?.getCurrentPosition(
                  (p) => { setForm((f) => ({ ...f, lat: p.coords.latitude.toFixed(6), lng: p.coords.longitude.toFixed(6) })); toast.success("Lokasi diambil"); },
                  () => toast.error("Gagal mengambil lokasi perangkat"),
                )}
              >
                Ambil lokasi saya
              </Button>
            </div>
            <Field label="Catatan" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="node-note" />
          </div>
          <Button type="submit" disabled={busy} data-testid="node-submit" className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">
            <Plus className="mr-1 h-4 w-4" /> Simpan Node
          </Button>
        </form>
      </TabsContent>

      <TabsContent value="backup">
        <div className="grid gap-5 lg:grid-cols-2" data-testid="infra-backup">
          <div className="space-y-4 rounded-2xl border border-border bg-card p-5">
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-cyan-400 to-violet-600">
                <Database className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="font-display text-base font-semibold">Backup Infrastruktur</p>
                <p className="text-[11px] text-muted-foreground">
                  Simpan seluruh data Server/POP, Backbone, ODC, ODP, dan ONU (termasuk koordinat peta, port/core, secret PPPoE, dan redaman) ke satu file JSON.
                </p>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-secondary/30 p-3 text-xs">
              <span className="font-mono text-lg font-semibold">{nodes.length}</span>
              <span className="ml-2 text-muted-foreground">node siap dibackup</span>
            </div>
            <Button onClick={downloadBackup} disabled={busy} className="w-full rounded-xl bg-gradient-to-r from-cyan-500 to-violet-600 text-white" data-testid="infra-backup-download">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <HardDriveDownload className="mr-2 h-4 w-4" />}
              Unduh Backup Infrastruktur (.json)
            </Button>
          </div>

          <div className="space-y-4 rounded-2xl border border-border bg-card p-5">
            <p className="font-display text-base font-semibold">Restore Infrastruktur</p>
            <div className="space-y-2">
              {[["merge", "Gabung (Upsert)", "Node dengan ID sama ditimpa, node lain tetap ada."], ["replace", "Ganti Total", "Semua node saat ini dihapus lalu diganti isi file backup."]].map(([v, l, d]) => (
                <button
                  key={v} type="button" onClick={() => setBk((b) => ({ ...b, mode: v }))} data-testid={`infra-mode-${v}`}
                  className={`w-full rounded-xl border p-3 text-left transition-colors ${bk.mode === v ? "border-violet-500/50 bg-violet-500/10" : "border-border hover:bg-secondary"}`}
                >
                  <p className="text-sm font-medium">{l}</p>
                  <p className="text-[11px] text-muted-foreground">{d}</p>
                </button>
              ))}
            </div>
            <input
              type="file" accept=".json,application/json" data-testid="infra-restore-file"
              onChange={(e) => setBk((b) => ({ ...b, file: e.target.files?.[0] || null, result: null }))}
              className="block w-full cursor-pointer rounded-xl border border-border bg-secondary/50 p-2.5 text-xs file:mr-3 file:rounded-lg file:border-0 file:bg-violet-600 file:px-3 file:py-1.5 file:text-white"
            />
            <Button onClick={restoreBackup} disabled={busy || !bk.file} className="w-full rounded-xl" data-testid="infra-restore-submit">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <HardDriveUpload className="mr-2 h-4 w-4" />}
              Mulai Restore
            </Button>
            {bk.result && (
              <div className="grid grid-cols-3 gap-2 text-center" data-testid="infra-restore-result">
                {[["Dipulihkan", bk.result.restored], ["Dihapus", bk.result.deleted], ["Dilewati", bk.result.skipped]].map(([l, v]) => (
                  <div key={l} className="rounded-xl border border-border p-2">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{l}</p>
                    <p className="font-mono text-base font-semibold">{v}</p>
                  </div>
                ))}
              </div>
            )}
            <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-[11px] text-amber-600 dark:text-amber-300">
              File backup infrastruktur berbeda dari backup database penuh di menu Backup &amp; Restore. Gunakan file yang diunduh dari halaman ini.
            </p>
          </div>

          <div className="space-y-3 rounded-2xl border border-border bg-card p-5 lg:col-span-2" data-testid="infra-snapshots">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-display text-base font-semibold">Snapshot di Server ({snaps.length})</p>
              <p className="text-[11px] text-muted-foreground">Mode restore aktif: <b className="text-foreground">{bk.mode === "replace" ? "Ganti Total" : "Gabung"}</b></p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Input
                className="h-10 max-w-xs rounded-xl bg-secondary/50 text-xs" placeholder="Catatan snapshot (opsional)"
                value={snapNote} onChange={(e) => setSnapNote(e.target.value)} data-testid="infra-snap-note"
              />
              <Button onClick={makeSnapshot} disabled={busy} className="rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 text-white" data-testid="infra-snap-create">
                Simpan Snapshot Sekarang
              </Button>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-[11px]">
                <thead className="bg-secondary/60">
                  <tr className="text-left">
                    <th className="px-2 py-1.5">Waktu</th><th className="px-2 py-1.5">Oleh</th><th className="px-2 py-1.5">Catatan</th>
                    <th className="px-2 py-1.5">Node</th><th className="px-2 py-1.5 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {!snaps.length && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Belum ada snapshot infrastruktur.</td></tr>}
                  {snaps.map((sn) => (
                    <tr key={sn._id} className="border-t border-border/60" data-testid={`infra-snap-${sn._id}`}>
                      <td className="px-2 py-1.5 font-mono">{dt(sn.created_at)}</td>
                      <td className="px-2 py-1.5">{sn.created_by}</td>
                      <td className="px-2 py-1.5 text-muted-foreground">{sn.note || "-"}</td>
                      <td className="px-2 py-1.5 font-mono">{sn.total}</td>
                      <td className="px-2 py-1.5 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" className="h-7 rounded-full text-[10px]" onClick={() => snapDownload(sn)} data-testid={`infra-snap-download-${sn._id}`}>Unduh</Button>
                          <Button size="sm" variant="outline" className="h-7 rounded-full text-[10px]" onClick={() => snapRestore(sn)} data-testid={`infra-snap-restore-${sn._id}`}>Restore</Button>
                          <Button size="sm" variant="ghost" className="h-7 rounded-full text-[10px] text-destructive" onClick={() => snapDelete(sn)} data-testid={`infra-snap-delete-${sn._id}`}>Hapus</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="import">
        <div className="grid gap-5 lg:grid-cols-2" data-testid="infra-import">
          <div className="space-y-4 rounded-2xl border border-border bg-card p-5">
            <p className="font-display text-base font-semibold">Impor Node dari CSV</p>
            <div className="rounded-xl border border-border bg-secondary/30 p-3 text-[11px] leading-relaxed text-muted-foreground">
              Kolom yang dikenali: <b className="text-foreground">nama, jenis, induk, core, port, secret_pppoe, alamat, catatan, lat, lng</b>.
              Kolom <b className="text-foreground">jenis</b> harus salah satu dari server, backbone, odc, odp, onu.
              Kolom <b className="text-foreground">induk</b> diisi NAMA node induk (boleh baris di atasnya pada file yang sama).
              <Button size="sm" variant="outline" className="mt-2 h-7 rounded-full text-[11px]" onClick={downloadTemplate} data-testid="infra-template">
                Unduh template CSV
              </Button>
            </div>
            <input
              type="file" accept=".csv,text/csv" data-testid="infra-csv-file"
              onChange={(e) => { const f = e.target.files?.[0] || null; setImp((c) => ({ ...c, file: f, preview: null, result: null })); previewCsv(f); }}
              className="block w-full cursor-pointer rounded-xl border border-border bg-secondary/50 p-2.5 text-xs file:mr-3 file:rounded-lg file:border-0 file:bg-emerald-600 file:px-3 file:py-1.5 file:text-white"
            />
            <label className="flex items-center justify-between gap-3 rounded-xl border border-border p-3 text-xs">
              <span>Perbarui node bila nama sudah ada
                <span className="block text-[11px] text-muted-foreground">Jika tidak dicentang, nama yang sudah ada akan dilewati</span>
              </span>
              <input
                type="checkbox" checked={imp.update} data-testid="infra-csv-update" className="h-4 w-4"
                onChange={(e) => { setImp((c) => ({ ...c, update: e.target.checked })); previewCsv(imp.file, e.target.checked); }}
              />
            </label>
            <Button onClick={importCsv} disabled={busy || !imp.file} data-testid="infra-csv-submit" className="w-full rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 text-white">
              {imp.preview ? "Simpan Impor Sekarang" : "Mulai Impor"}
            </Button>
            {imp.result && (
              <div className="grid grid-cols-4 gap-2 text-center" data-testid="infra-csv-result">
                {[["Baris", imp.result.total_rows], ["Baru", imp.result.created], ["Update", imp.result.updated], ["Lewat", imp.result.skipped]].map(([l, v]) => (
                  <div key={l} className="rounded-xl border border-border p-2">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{l}</p>
                    <p className="font-mono text-base font-semibold">{v}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-3 rounded-2xl border border-border bg-card p-5">
            <p className="font-display text-base font-semibold">Pratinjau</p>
            {imp.previewing && <p className="text-xs text-muted-foreground">Membaca pratinjau…</p>}
            {!imp.preview && !imp.previewing && <p className="text-xs text-muted-foreground">Pilih file CSV untuk melihat data yang akan disimpan.</p>}
            {imp.preview && (
              <>
                <p className="text-xs font-medium">
                  {imp.preview.total_rows} baris · <span className="text-emerald-500">{imp.preview.created} baru</span> ·{" "}
                  <span className="text-cyan-500">{imp.preview.updated} diperbarui</span> ·{" "}
                  <span className="text-muted-foreground">{imp.preview.skipped} dilewati</span>
                </p>
                <div className="max-h-64 overflow-auto rounded-xl border border-border" data-testid="infra-csv-preview">
                  <table className="w-full text-[11px]">
                    <thead className="sticky top-0 bg-secondary/80">
                      <tr className="text-left">
                        <th className="px-2 py-1">Brs</th><th className="px-2 py-1">Nama</th><th className="px-2 py-1">Jenis</th>
                        <th className="px-2 py-1">Induk</th><th className="px-2 py-1">Core/Port</th><th className="px-2 py-1">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(imp.preview.preview || []).map((r) => (
                        <tr key={r.row} className="border-t border-border/60">
                          <td className="px-2 py-1 font-mono">{r.row}</td>
                          <td className="px-2 py-1">{r.name}</td>
                          <td className="px-2 py-1 uppercase">{r.type}</td>
                          <td className="px-2 py-1">{r.parent}</td>
                          <td className="px-2 py-1 font-mono">{r.cores}/{r.ports}</td>
                          <td className="px-2 py-1">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] ${r.action === "baru" ? "bg-emerald-500/15 text-emerald-500" : "bg-cyan-500/15 text-cyan-500"}`}>{r.action}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!!(imp.preview.errors || []).length && (
                  <div className="max-h-32 space-y-1 overflow-y-auto rounded-xl border border-amber-500/30 bg-amber-500/5 p-2">
                    {imp.preview.errors.map((er, i) => (
                      <p key={i} className="text-[11px] text-amber-600 dark:text-amber-300">Baris {er.row} · {er.name}: {er.reason}</p>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </TabsContent>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-node-dialog">
          <DialogHeader><DialogTitle>Ubah Node</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Nama" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="edit-node-name" />
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Induk</Label>
                <select className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="edit-node-parent" value={edit.parent_id || ""} onChange={(e) => setEdit({ ...edit, parent_id: e.target.value })}>
                  <option value="">— tanpa induk —</option>
                  {nodes.filter((n) => n.id !== edit.id).map((n) => <option key={n.id} value={n.id}>{n.type.toUpperCase()} · {n.name}</option>)}
                </select>
              </div>
              <Field label="Core" type="number" value={edit.cores || 0} onChange={(e) => setEdit({ ...edit, cores: e.target.value })} data-testid="edit-node-cores" />
              <Field label="Port" type="number" value={edit.ports || 0} onChange={(e) => setEdit({ ...edit, ports: e.target.value })} data-testid="edit-node-ports" />
              <Field label="Alamat" value={edit.address || ""} onChange={(e) => setEdit({ ...edit, address: e.target.value })} data-testid="edit-node-address" />
              <Field label="Latitude" value={edit.lat ?? ""} onChange={(e) => setEdit({ ...edit, lat: e.target.value })} data-testid="edit-node-lat" />
              <Field label="Longitude" value={edit.lng ?? ""} onChange={(e) => setEdit({ ...edit, lng: e.target.value })} data-testid="edit-node-lng" />
              <Field label="Catatan" value={edit.note || ""} onChange={(e) => setEdit({ ...edit, note: e.target.value })} data-testid="edit-node-note" />
              <div className="sm:col-span-2">
                <Button onClick={save} disabled={busy} className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white" data-testid="edit-node-save">Simpan</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!signal} onOpenChange={(o) => !o && setSignal(null)}>
        <DialogContent className="max-w-sm" data-testid="signal-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Activity className="h-4 w-4" /> Redaman {signal?.name}</DialogTitle></DialogHeader>
          {signal && (
            <div className="space-y-4">
              <Field label="Rx Power (dBm)" type="number" step="0.1" value={signal.rx_power} onChange={(e) => setSignal({ ...signal, rx_power: e.target.value })} data-testid="signal-input" />
              <Field label="Catatan" value={signal.note} onChange={(e) => setSignal({ ...signal, note: e.target.value })} data-testid="signal-note" />
              <p className="text-xs text-muted-foreground">Hijau ≥ -25 dBm · Kuning -25,1 s/d -27 dBm · Merah &lt; -27 dBm</p>
              <Button onClick={saveSignal} disabled={busy} className="rounded-full bg-gradient-to-r from-emerald-500 to-cyan-600 text-white" data-testid="signal-save">Simpan Redaman</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Tabs>
  );
}
